
import { forwardRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock } from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/hooks/useAuth";

interface TimeSlot {
  id: string;
  start_time: string;
  max_bookings: number;
  current_bookings: number;
  womens_only: boolean;
}

interface BookingTimeSelectionProps {
  selectedDate: Date;
  selectedTimeSlot: string;
  onSelectTimeSlot: (timeSlotId: string) => void;
  availableTimeSlots?: TimeSlot[];
  timeSlotsLoading: boolean;
}

// Female gender symbol component
const FemaleIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <circle cx="12" cy="8" r="6" stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M12 14v6" stroke="currentColor" strokeWidth="2"/>
    <path d="M9 19h6" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const BookingTimeSelection = forwardRef<HTMLDivElement, BookingTimeSelectionProps>(({ 
  selectedDate, 
  selectedTimeSlot, 
  onSelectTimeSlot, 
  availableTimeSlots, 
  timeSlotsLoading 
}, ref) => {
  const { user } = useAuth();
  
  console.log('BookingTimeSelection: Rendering with slots', {
    availableTimeSlots: availableTimeSlots?.map(slot => ({
      id: slot.id,
      start_time: slot.start_time,
      current_bookings: slot.current_bookings,
      max_bookings: slot.max_bookings,
      womens_only: slot.womens_only,
      spotsLeft: slot.max_bookings - slot.current_bookings
    })),
    selectedTimeSlot,
    timeSlotsLoading
  });

  // Get user gender from profile metadata
  const userGender = user?.user_metadata?.gender;
  const isUserFemale = userGender === 'female';

  const handleTimeSlotClick = (timeSlot: TimeSlot) => {
    // Check if this is a women-only slot and user is not female
    if (timeSlot.womens_only && !isUserFemale) {
      // Don't allow selection - the button will be disabled anyway
      return;
    }
    
    onSelectTimeSlot(timeSlot.id);
  };

  return (
    <Card ref={ref}>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Clock className="h-5 w-5" />
          <span>Select Time</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {timeSlotsLoading ? (
          <div className="animate-pulse">
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="h-12 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        ) : availableTimeSlots && availableTimeSlots.length > 0 ? (
          <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
            {availableTimeSlots.map((timeSlot) => {
              // Parse the UTC time from database and convert to local time for display
              const startTime = new Date(timeSlot.start_time);
              const timeString = format(startTime, "HH:mm");
              const isSelected = selectedTimeSlot === timeSlot.id;
              const spotsLeft = timeSlot.max_bookings - timeSlot.current_bookings;
              const isWomensOnly = timeSlot.womens_only;
              const isDisabledForUser = isWomensOnly && !isUserFemale;
              
              console.log('BookingTimeSelection: Rendering time slot button', {
                slotId: timeSlot.id,
                timeString,
                currentBookings: timeSlot.current_bookings,
                maxBookings: timeSlot.max_bookings,
                spotsLeft,
                isSelected,
                isWomensOnly,
                userGender,
                isUserFemale,
                isDisabledForUser
              });
              
              return (
                <div key={timeSlot.id} className="relative">
                  <Button
                    variant={isSelected ? "default" : "outline"}
                    onClick={() => handleTimeSlotClick(timeSlot)}
                    className={`h-16 w-full flex flex-col justify-center relative ${
                      isDisabledForUser ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    disabled={spotsLeft === 0 || isDisabledForUser}
                  >
                    <div className="font-medium">{timeString}</div>
                    <div className="text-xs opacity-70">
                      {spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} left
                    </div>
                    {isWomensOnly && (
                      <Badge 
                        variant="secondary" 
                        className="absolute -top-1 -right-1 text-xs px-1 py-0 h-5 flex items-center gap-1"
                      >
                        <FemaleIcon className="h-2 w-2" />
                        W
                      </Badge>
                    )}
                  </Button>
                  {isDisabledForUser && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-500/10 rounded-md">
                      <span className="text-xs text-gray-600 font-medium">Women Only</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No available time slots for this date</p>
            <p className="text-sm mt-2">Please select a different date</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

BookingTimeSelection.displayName = "BookingTimeSelection";

export default BookingTimeSelection;
